import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { TerritoryCircleFlag } from '~/components/TerritoryCircleFlag/TerritoryCircleFlag';
import { cn } from '~/lib/cn';
import type {
  ActiveType,
  Market,
  Markets,
  Platform,
  Platforms,
} from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlan/components/BudgetDistribution/types';
import {
  getMarketSliceColor,
  getPlatformSliceColor,
} from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlan/components/BudgetDistribution/utils/getSliceColor';
import { PLATFORM_MAP } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/constants';
import { formatMillionsNumber } from '~/modules/artist-roster/utils/formatMillionsNumber';

export interface BudgetLegendProps {
  activeType: ActiveType;
  totalBudget: number;
  data: Markets | Platforms;
  hoveredIndex: number | null;
  setHoveredIndex: (index: number | null) => void;
}

export function BudgetLegend({
  activeType,
  totalBudget,
  data,
  hoveredIndex,
  setHoveredIndex,
}: BudgetLegendProps) {
  const { t: nameTranslation } = useTranslation('names');

  return (
    <div className="cursor-pointer self-center">
      {data.map((item, index) => {
        const itemName =
          activeType === 'platforms'
            ? nameTranslation(PLATFORM_MAP[(item as Platform).name].labelKey)
            : (item as Market).name;

        const sliceColor =
          activeType === 'platforms'
            ? getPlatformSliceColor((item as Platform).name)
            : getMarketSliceColor(index);

        return (
          <div
            key={item.name}
            aria-label={itemName}
            className="py-px"
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div
              className={cn(
                'flex items-center rounded-sm border-y-px p-md text-body-sm font-semibold leading-none transition-opacity duration-300',
                'border-t-emphasis-subtle border-b-transparent',
                'bg-[linear-gradient(86deg,oklch(var(--bg-emphasis-default))16.42%,oklch(var(--bg-emphasis-subtle))82.53%)]',
                {
                  'opacity-stronger':
                    hoveredIndex !== null && hoveredIndex !== index,
                },
              )}
            >
              <div
                className={cn(
                  'mr-sm size-sm rounded-full duration-300 ease-out',
                  {
                    'scale-150': hoveredIndex === index,
                  },
                )}
                style={{
                  backgroundColor: sliceColor,
                }}
              />
              <div className="mr-xs flex w-lg justify-center text-center">
                {activeType === 'platforms' ? (
                  <Icon type={(item as Platform).name} variant="brands" />
                ) : null}
                {activeType === 'markets' ? (
                  <TerritoryCircleFlag
                    code={(item as Market).territoryCode}
                    className="-my-xs size-md rounded-full outline outline-2 bg-emphasis-subtle outline-emphasis-subtle"
                  />
                ) : null}
              </div>
              <div className="mr-sm">{itemName}</div>
              <div className="ml-auto min-w-[46px] text-right">
                ${formatMillionsNumber(item.budget, true)}
              </div>
              <div className="w-[54px] text-right">
                {((item.budget / totalBudget) * 100).toFixed(0)}%
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

BudgetLegend.displayName = 'BudgetLegend';
